import React, { useEffect, useState } from 'react';
import tokens from '../design/tokens';
import { ISO_THRESHOLDS, getSizeClass, isoBands, gradeFromVelocity } from '../services/monitoringApi';

/**
 * The instrument-panel trend chart — the same object as the homepage hero, but
 * driven by a real motor's telemetry.
 *
 * Inline SVG on purpose: it is the one chart in the product, it has to print
 * (the PDF draws the same shape), and it must look identical on the site and in
 * the portal. ISO zone bands come from the motor's own size class, so a 15 HP
 * pump and a 150 HP drive are each graded against the right thresholds.
 *
 * Props:
 *   readings  [{ timestamp, rmsVelocity }]  ascending by time
 *   hp        motor horsepower (picks the ISO size class)
 *   alerts    [{ timestamp, label }]        optional markers (health-check fires)
 *   ndeReadings optional second trace
 */

const C = tokens.color;
const F = tokens.font;
const G = tokens.grade;

// Wide layout, and a phone layout (below 560px) with room for readable labels.
// Zone labels live in a gutter right of the plot (PLOT.x1 → VB.w), so the trace
// and the alert line can never cross them. Wide: word + range. Phone: the zone
// letter only — "UNSATISFACTORY" would take a third of a 360-wide plot.
const LAYOUTS = {
  wide:    { VB: { w: 700, h: 300 }, PLOT: { x0: 56, x1: 596, y0: 30, y1: 240 }, fs: 10, tick: 11, zoneWords: true },
  compact: { VB: { w: 360, h: 270 }, PLOT: { x0: 44, x1: 326, y0: 32, y1: 206 }, fs: 12, tick: 12, zoneWords: false },
};

function useCompact() {
  const q = '(max-width: 559px)';
  const [compact, setCompact] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(q).matches);
  useEffect(() => {
    const m = window.matchMedia?.(q);
    if (!m) return undefined;
    const on = () => setCompact(m.matches);
    m.addEventListener?.('change', on);
    return () => m.removeEventListener?.('change', on);
  }, []);
  return compact;
}

export default function InstrumentChart({
  readings = [], ndeReadings = null, hp, alerts = [], label,
}) {
  const { VB, PLOT, fs, tick, zoneWords } = LAYOUTS[useCompact() ? 'compact' : 'wide'];
  const t = ISO_THRESHOLDS[getSizeClass(hp)] || ISO_THRESHOLDS.small;
  const vals = readings.map((r) => r.rmsVelocity ?? 0).filter((v) => v != null);
  const ndeVals = (ndeReadings || []).map((r) => r.rmsVelocity ?? 0);

  const bands = isoBands(hp);
  // Y scale: always reach past the unsatisfactory ceiling, so all four ISO
  // zones are on the chart even when a motor is quiet.
  const dataMax = Math.max(0, ...vals, ...ndeVals);
  const yMax = Math.max(t.unsatisfactory * 1.15, dataMax * 1.15, t.good * 2);
  const yFor = (v) => PLOT.y1 - (Math.min(v, yMax) / yMax) * (PLOT.y1 - PLOT.y0);
  const xFor = (i, n) => (n <= 1 ? PLOT.x0 : PLOT.x0 + (i / (n - 1)) * (PLOT.x1 - PLOT.x0));

  const pathFor = (arr) => arr.map((r, i) => {
    const x = xFor(i, arr.length);
    const y = yFor(r.rmsVelocity ?? 0);
    return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');

  const dePath = readings.length > 1 ? pathFor(readings) : null;
  const ndePath = ndeReadings && ndeReadings.length > 1 ? pathFor(ndeReadings) : null;

  const last = vals.length ? vals[vals.length - 1] : null;
  // The line takes the colour of the grade the latest reading earns — the same
  // letter the card and the report show.
  const lastGrade = gradeFromVelocity(last, hp);
  const lineColor = lastGrade ? G[lastGrade].stroke : C.textMuted;

  const firstTs = readings[0]?.timestamp;
  const lastTs = readings[readings.length - 1]?.timestamp;
  const fmt = (ts) => (ts ? new Date(ts).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '');

  const aria = label || (last != null
    ? `Vibration trend: latest reading ${last.toFixed(2)} millimetres per second, against an ISO 20816 unsatisfactory threshold of ${t.unsatisfactory} for this asset size`
    : 'Vibration trend: no readings for this period');

  if (readings.length === 0) {
    return (
      <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, fontSize: 14 }}>
        No readings for this period.
      </div>
    );
  }

  return (
    <svg width="100%" viewBox={`0 0 ${VB.w} ${VB.h}`} fill="none" role="img" aria-label={aria}
         style={{ display: 'block', height: 'auto' }}>
      <defs>
        {/* The one sanctioned gradient: the area fill under the trend line. */}
        <linearGradient id="bs-chartfill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor={lineColor} stopOpacity="0.22" />
          <stop offset="1" stopColor={lineColor} stopOpacity="0" />
        </linearGradient>
      </defs>

      {/* ISO zone bands, from this motor's size class — the grading thresholds */}
      {bands.map((b) => {
        const top = yFor(Math.min(b.hi, yMax));
        const bottom = yFor(b.lo);
        return (
          <g key={b.zone}>
            <rect x={PLOT.x0} y={top} width={PLOT.x1 - PLOT.x0} height={bottom - top}
                  fill={G[b.grade].bg} fillOpacity="0.75" />
          </g>
        );
      })}

      {/* axes */}
      <path d={`M${PLOT.x0} ${PLOT.y0} V${PLOT.y1} H${PLOT.x1}`} stroke={C.ink} strokeOpacity="0.25" />
      <g fontFamily={F.mono} fontSize={tick} fill={C.textMuted}>
        <text x="4" y={PLOT.y0 + 4}>{yMax.toFixed(1)}</text>
        <text x="4" y={yFor(t.unsatisfactory) + 4}>{t.unsatisfactory}</text>
        <text x="4" y={yFor(t.satisfactory) + 4}>{t.satisfactory}</text>
        <text x="4" y={yFor(t.good) + 4}>{t.good}</text>
        <text x="4" y={PLOT.y1 + 4}>0</text>
        <text x={PLOT.x0} y={PLOT.y1 + 22}>{fmt(firstTs)}</text>
        <text x={PLOT.x1} y={PLOT.y1 + 22} textAnchor="end">{fmt(lastTs)}</text>
        <text x="4" y={VB.h - 10} fill={C.ink} fontWeight="500">mm/s RMS</text>
      </g>

      {/* NDE trace, when a second sensor is present */}
      {ndePath && <path d={ndePath} stroke={C.link} strokeWidth="2" strokeLinecap="round" />}

      {/* DE trace + area fill */}
      {dePath && (
        <>
          <path d={`${dePath} L${PLOT.x1} ${PLOT.y1} L${PLOT.x0} ${PLOT.y1} Z`} fill="url(#bs-chartfill)" />
          <path d={dePath} stroke={lineColor} strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
        </>
      )}

      {/* Zone labels in the right gutter, outside the plot — the trace and the
          alert line never reach them. Each sits on a tab in its band's colour. */}
      {bands.map((b) => {
        const top = yFor(Math.min(b.hi, yMax));
        const bottom = yFor(b.lo);
        const h = bottom - top;
        if (h < fs + 2) return null;
        const gx = PLOT.x1 + 4;
        const gw = VB.w - 4 - gx;
        const mid = top + h / 2;
        // "UNACCEPTABLE > 7.1" -> word "UNACCEPTABLE", range "> 7.1" (split at the first space only).
        const cut = b.label.indexOf(' ');
        const word = b.label.slice(0, cut), range = b.label.slice(cut + 1);
        const twoLines = zoneWords && h >= fs * 2 + 6;
        return (
          <g key={`l-${b.zone}`} fontFamily={F.mono} fontSize={fs} fill={G[b.grade].fg}>
            <rect x={gx} y={top + 1} width={gw} height={h - 2} rx="3" fill={G[b.grade].bg} />
            {!zoneWords ? (
              <text x={gx + gw / 2} y={mid + fs * 0.35} textAnchor="middle" fontWeight="700">{b.grade}</text>
            ) : twoLines ? (
              <>
                <text x={gx + 5} y={mid - 2}>{word}</text>
                <text x={gx + 5} y={mid + fs}>{range}</text>
              </>
            ) : (
              <text x={gx + 5} y={mid + fs * 0.35}>{word}</text>
            )}
          </g>
        );
      })}

      {/* Alert markers — where the health check actually fired */}
      {alerts.map((a, i) => {
        const idx = readings.findIndex((r) => new Date(r.timestamp) >= new Date(a.timestamp));
        if (idx < 0) return null;
        const x = xFor(idx, readings.length);
        return (
          <g key={i}>
            <path d={`M${x} ${PLOT.y0} V${PLOT.y1}`} stroke={C.ink} strokeWidth="1.2" strokeDasharray="4 4" />
            {/* Above the plot, so it can never sit on a zone label. */}
            <rect x={Math.min(Math.max(PLOT.x0, x - 43), PLOT.x1 - 86)} y="4" width="86" height="22" rx="11" fill={C.ink} />
            <text x={Math.min(Math.max(PLOT.x0, x - 43), PLOT.x1 - 86) + 43} y="19" textAnchor="middle"
                  fontFamily={F.body} fontSize={tick} fontWeight="600" fill="#FFFFFF">
              {a.label || 'Alert'}
            </text>
            <circle cx={x} cy={yFor(readings[idx].rmsVelocity ?? 0)} r="5" fill={lineColor} stroke="#FFFFFF" strokeWidth="2" />
          </g>
        );
      })}
    </svg>
  );
}
